import { useState } from 'react';
import type { ExtractedDataUI } from '../types';
import clsx from 'clsx';

interface ExtractedFieldsProps {
  extractedData?: ExtractedDataUI;
  onFieldUpdate?: (fieldName: string, value: any) => void;
  isUpdating?: boolean;
}

export default function ExtractedFields({ extractedData, onFieldUpdate, isUpdating }: ExtractedFieldsProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  if (!extractedData) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">No extracted data available</p>
      </div>
    );
  }

  const handleEdit = (fieldName: string, currentValue: any) => {
    setEditingField(fieldName);
    setEditValue(String(currentValue || ''));
  };

  const handleSave = (fieldName: string) => {
    if (onFieldUpdate) {
      onFieldUpdate(fieldName, editValue);
    }
    setEditingField(null);
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600';
    if (confidence >= 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatFieldName = (name: string) => {
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const fieldGroups: Record<string, string[]> = {
    identification: ['invoiceNumber', 'documentNumber', 'referenceNumber', 'contractNumber'],
    parties: ['supplierName', 'customerName', 'merchantName', 'parties', 'accountHolder'],
    dates: ['documentDate', 'dueDate', 'effectiveDate', 'expirationDate', 'statementPeriod', 'transactionDate'],
    amounts: ['totalAmount', 'subtotal', 'taxAmount', 'currency', 'paymentAmount', 'openingBalance', 'closingBalance'],
    payment: ['paymentMethod', 'paymentTerms', 'iban', 'bankAccount'],
    other: [], // Catch-all for remaining fields
  };

  // Organize fields by groups
  const organizedFields: Record<string, Array<[string, any]>> = {};
  
  Object.entries(extractedData.fields).forEach(([fieldName, field]) => {
    let grouped = false;
    for (const [groupName, fieldList] of Object.entries(fieldGroups)) {
      if (fieldList.includes(fieldName)) {
        if (!organizedFields[groupName]) {
          organizedFields[groupName] = [];
        }
        organizedFields[groupName].push([fieldName, field]);
        grouped = true;
        break;
      }
    }
    if (!grouped) {
      if (!organizedFields.other) {
        organizedFields.other = [];
      }
      organizedFields.other.push([fieldName, field]);
    }
  });

  return (
    <div className="space-y-6">
      {/* Document Type Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Document Classification</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">Type:</span>
          <span className="text-sm text-gray-900 capitalize">{extractedData.documentType}</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-medium text-gray-500">Confidence:</span>
          <span className={clsx('text-sm font-medium', getConfidenceColor(extractedData.confidence))}>
            {(extractedData.confidence * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Extracted Fields by Group */}
      {Object.entries(organizedFields).map(([groupName, fields]) => {
        if (fields.length === 0) return null;
        
        return (
          <div key={groupName} className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 capitalize">
              {groupName === 'other' ? 'Additional Fields' : groupName}
            </h3>
            <dl className="space-y-4">
              {fields.map(([fieldName, field]) => (
                <div key={fieldName} className="border-b border-gray-200 pb-4 last:border-0">
                  <dt className="text-sm font-medium text-gray-500 mb-1">
                    {formatFieldName(fieldName)}
                  </dt>
                  <dd className="mt-1">
                    {editingField === fieldName ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          disabled={isUpdating}
                        />
                        <button
                          onClick={() => handleSave(fieldName)}
                          disabled={isUpdating}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={isUpdating}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="text-sm text-gray-900">
                            {field.value !== null && field.value !== undefined
                              ? String(field.value)
                              : '(empty)'}
                          </span>
                          <span className={clsx('ml-2 text-xs', getConfidenceColor(field.confidence))}>
                            ({(field.confidence * 100).toFixed(0)}%)
                          </span>
                        </div>
                        {onFieldUpdate && (
                          <button
                            onClick={() => handleEdit(fieldName, field.value)}
                            className="ml-4 text-sm text-primary-600 hover:text-primary-900"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    )}
                    {field.evidence && field.evidence.length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Evidence: {field.evidence.join(', ')}
                      </p>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}